<template>
    <div class="space-y-4">
        <div>
            <label class="block mb-2 text-sm font-medium text-gray-700" :for="`${idPrefix}FontFamily`">
                {{ fontLabel }}
            </label>
            <select
                :id="`${idPrefix}FontFamily`"
                v-model="model.font_family"
                class="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 shadow-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-200"
                :style="{ fontFamily: model.font_family }"
            >
                <option
                    v-for="font in fontOptions"
                    :key="font.value"
                    :value="font.value"
                    :style="{ fontFamily: font.value }"
                >
                    {{ font.label }}
                </option>
            </select>
            <p v-if="hint" class="mt-1.5 text-xs text-gray-500">{{ hint }}</p>
        </div>

        <div class="card-typography-color-panel rounded-xl border border-gray-200 bg-gray-50/80 p-4">
            <label class="block mb-2 text-sm font-medium text-gray-700" :for="`${idPrefix}FontColor`">
                {{ __('Text Color') }}
            </label>
            <div class="flex items-center gap-3">
                <div
                    class="card-typography-color-preview shrink-0"
                    :style="{
                        backgroundColor: normalizeHexColor(model.font_color),
                        color: isLightHexColor(model.font_color) ? '#1e293b' : '#ffffff',
                    }"
                    aria-hidden="true"
                >
                    Aa
                </div>
                <input
                    :id="`${idPrefix}FontColor`"
                    v-model="model.font_color"
                    type="color"
                    class="card-typography-color-input"
                    :title="__('Pick a custom color')"
                >
                <input
                    type="text"
                    :value="model.font_color"
                    class="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm font-mono text-gray-900 shadow-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-200"
                    placeholder="#000000"
                    maxlength="7"
                    @input="model.font_color = $event.target.value"
                    @blur="normalizeFontColorField"
                >
            </div>

            <div class="mt-4 space-y-3">
                <p class="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {{ __('Web color presets') }}
                </p>
                <div v-for="group in colorPresetGroups" :key="group.label">
                    <span class="mb-1.5 block text-xs font-medium text-gray-500">{{ group.label }}</span>
                    <div class="flex flex-wrap gap-2">
                        <button
                            v-for="preset in group.colors"
                            :key="preset.value"
                            type="button"
                            class="card-typography-color-swatch"
                            :class="{
                                'card-typography-color-swatch--active': normalizeHexColor(model.font_color) === preset.value,
                                'card-typography-color-swatch--light': isLightHexColor(preset.value),
                            }"
                            :style="{ backgroundColor: preset.value }"
                            :title="`${preset.label} (${preset.value})`"
                            :aria-label="preset.label"
                            @click="setFontColor(preset.value)"
                        >
                            <span class="sr-only">{{ preset.label }}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div>
            <div class="flex items-center justify-between mb-2">
                <label class="text-sm font-medium text-gray-700" :for="`${idPrefix}FontSize`">
                    {{ __('Font Size') }}
                </label>
                <span class="text-sm font-semibold text-green-700 tabular-nums">{{ model.font_size }}px</span>
            </div>
            <input
                :id="`${idPrefix}FontSize`"
                v-model.number="model.font_size"
                type="range"
                :min="minSize"
                :max="maxSize"
                step="1"
                class="card-typography-range w-full"
            >
        </div>

        <div>
            <span class="block mb-2 text-sm font-medium text-gray-700">{{ __('Text Style') }}</span>
            <div class="grid grid-cols-3 gap-2">
                <label
                    class="flex items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium cursor-pointer transition-colors"
                    :class="model.font_bold ? 'border-green-600 bg-green-50 text-green-700' : 'border-gray-300 bg-white text-gray-700 hover:border-green-400'"
                >
                    <input v-model="model.font_bold" type="checkbox" class="sr-only">
                    <span class="font-bold">B</span>
                    <span>{{ __('Bold') }}</span>
                </label>
                <label
                    class="flex items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium cursor-pointer transition-colors italic"
                    :class="model.font_italic ? 'border-green-600 bg-green-50 text-green-700' : 'border-gray-300 bg-white text-gray-700 hover:border-green-400'"
                >
                    <input v-model="model.font_italic" type="checkbox" class="sr-only">
                    <span class="italic">I</span>
                    <span>{{ __('Italic') }}</span>
                </label>
                <label
                    class="flex items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium cursor-pointer transition-colors"
                    :class="model.text_shadow ? 'border-green-600 bg-green-50 text-green-700' : 'border-gray-300 bg-white text-gray-700 hover:border-green-400'"
                >
                    <input v-model="model.text_shadow" type="checkbox" class="sr-only">
                    <icon name="aperture" class="w-4 h-4" />
                    <span>{{ __('Shadow') }}</span>
                </label>
            </div>
        </div>

        <div v-if="model.text_shadow">
            <div class="flex items-center justify-between mb-2">
                <label class="text-sm font-medium text-gray-700" :for="`${idPrefix}ShadowBlur`">
                    {{ __('Shadow Strength') }}
                </label>
                <span class="text-sm font-semibold text-green-700 tabular-nums">{{ model.text_shadow_blur }}px</span>
            </div>
            <input
                :id="`${idPrefix}ShadowBlur`"
                v-model.number="model.text_shadow_blur"
                type="range"
                min="0"
                max="16"
                step="1"
                class="card-typography-range w-full"
            >
        </div>
    </div>
</template>

<script>
import Icon from '@/Shared/Icon'
import {
    CARD_FONT_OPTIONS,
    CARD_COLOR_PRESET_GROUPS,
    normalizeHexColor,
    isLightHexColor,
} from '@/utils/cardFontOptions'

export default {
    components: {
        Icon,
    },
    props: {
        model: {
            type: Object,
            required: true,
        },
        idPrefix: {
            type: String,
            default: 'card',
        },
        fontLabel: {
            type: String,
            default: 'Card Text Font',
        },
        hint: {
            type: String,
            default: '',
        },
        minSize: {
            type: Number,
            default: 12,
        },
        maxSize: {
            type: Number,
            default: 56,
        },
    },
    data() {
        return {
            fontOptions: CARD_FONT_OPTIONS,
            colorPresetGroups: CARD_COLOR_PRESET_GROUPS,
        }
    },
    methods: {
        normalizeHexColor,
        isLightHexColor,
        setFontColor(value) {
            this.model.font_color = value
        },
        normalizeFontColorField() {
            this.model.font_color = normalizeHexColor(this.model.font_color)
        },
    },
}
</script>

<style scoped>
.card-typography-range {
    accent-color: #16a34a;
    height: 0.375rem;
    cursor: pointer;
}

.card-typography-color-panel {
    background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
}

.card-typography-color-preview {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.75rem;
    height: 2.75rem;
    border-radius: 0.625rem;
    border: 1px solid #cbd5e1;
    font-size: 0.875rem;
    font-weight: 700;
    letter-spacing: -0.02em;
    box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.08);
}

.card-typography-color-input {
    width: 3rem;
    height: 2.75rem;
    flex-shrink: 0;
    cursor: pointer;
    border: 1px solid #d1d5db;
    border-radius: 0.625rem;
    padding: 0.125rem;
    background: #fff;
}

.card-typography-color-swatch {
    width: 2rem;
    height: 2rem;
    border-radius: 0.5rem;
    border: 2px solid transparent;
    cursor: pointer;
    transition: transform 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
}

.card-typography-color-swatch--light {
    border-color: #e2e8f0;
}

.card-typography-color-swatch:hover {
    transform: translateY(-1px) scale(1.04);
    border-color: #86efac;
}

.card-typography-color-swatch--active {
    border-color: #16a34a;
    box-shadow: 0 0 0 2px #fff, 0 0 0 4px #16a34a;
}
</style>
